import { FC, useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { AllieSearchbarProps } from './Searchbar.types';
import {
  StyledContainerSearch,
  StyledSuggestions,
  StyledSuggestionList,
  StyledSuggestionItem,
  StyledSearchbar,
  StyledMoreResultsItem,
  StyledAddItemList,
  StyledAddItem,
  StyledNoResultsItem,
} from './Searchbar.styles';
import { AllieText } from '../Text';
import { AllieAddIcon } from '../../styling/icons';

const SUGGESTION_HEIGHT = 34;
const MAX_SUGGESTION_INDEX = 9;

export const AllieSearchbar: FC<AllieSearchbarProps> = ({
  options,
  maxSuggestions = 8,
  minInputLength = 1,
  onSelect,
  onChange,
  onClear,
  value,
  disabled = false,
  hasBorder = true,
  limitedResultText,
  reset,
  addItem = false,
  addItemText,
  noResultsText,
  ...props
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focused, setFocused] = useState(false);
  const [selected, setSelected] = useState(false);
  const [selectedValue, setSelectedValue] = useState<any>(value || '');
  const [suggestionActive, setSuggestionActive] = useState<number>();
  const [preventChange, setPreventChange] = useState<boolean>(false);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLIonSearchbarElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const handleClear = (initialValue = '') => {
    onClear && onClear();
    setSelectedValue(initialValue);
    setSelected(false);
    setFocused(true);
  };

  useEffect(() => reset && handleClear(), [reset]);

  useEffect(() => {
    if (showSuggestions) {
      // Hide the results when the user clicks outside the input element and the dropdown list.
      document.addEventListener('click', (e) => {
        if (!searchRef.current?.contains(e.target as Node)) {
          setFocused(false);
        }
      });
      const content = document.getElementById('main-app-content');
      content?.addEventListener('ionScroll', (e) => {
        if (!searchRef.current?.contains(e.target as Node)) {
          setShowSuggestions(false);
        }
      });
    }
  }, [showSuggestions]);

  const limitedOptions = options?.slice(0, maxSuggestions);

  const scrollTo = (offset: 'up' | 'down' | 'top' | 'bottom' = 'top') => {
    const el = document.getElementById('suggestions-list');

    if (el) {
      let top = 0;
      switch (offset) {
        case 'bottom': {
          top = el.scrollHeight;
          break;
        }
        case 'down': {
          top = el?.scrollTop + SUGGESTION_HEIGHT;
          break;
        }
        case 'up': {
          top = el?.scrollTop - SUGGESTION_HEIGHT;
          break;
        }
      }

      el?.scroll({ top });
    }
  };

  useEffect(() => {
    if (value) {
      setFocused(false);
      setSelected(true);
      setPreventChange(true);
    }
  }, [value]);

  useEffect(() => {
    if (value !== selectedValue) {
      onChange?.(selectedValue);
    }
  }, [selectedValue]);

  useEffect(() => {
    if (showSuggestions && resultsRef.current && searchRef.current) {
      const searchRect = searchRef.current.getBoundingClientRect();
      const results = resultsRef.current;
      results.style.top = searchRect.top + searchRect.height + 'px';
      results.style.left = searchRect.left + 'px';
      results.style.width = searchRect.width + 'px';
    }
  }, [showSuggestions]);

  const handleFocus = () => setFocused(true);

  useEffect(() => {
    setShowSuggestions(focused && selectedValue.length >= minInputLength);
  }, [focused, selectedValue]);

  const handleSelect = (option: { value: string | number; text: string }) => {
    // setPreventChange(true);
    onSelect && onSelect(option.value as string);
  };

  const handleChange = (event: any) => {
    if (!preventChange) {
      const tempValue = event.detail.value;

      if (firstLoad) {
        tempValue.length === 1 && setSelected(false);
        setSelectedValue(tempValue);
        setFirstLoad(false);
      } else {
        if (!tempValue.length) {
          handleClear();
        } else {
          if (selected) {
            handleClear(tempValue);
          } else {
            setSelectedValue(tempValue);
          }
        }
      }
    } else {
      setPreventChange(false);
    }
  };

  const onKeyUpHandler = (e: any) => {
    if (limitedOptions) {
      switch (e.key) {
        case 'ArrowDown': {
          if (suggestionActive === undefined || suggestionActive === limitedOptions.length - 1) {
            setSuggestionActive(0);
            scrollTo('top');
          } else {
            setSuggestionActive(suggestionActive + 1);
            if (suggestionActive && suggestionActive >= MAX_SUGGESTION_INDEX) scrollTo('down');
          }
          break;
        }
        case 'ArrowUp': {
          if (!suggestionActive) {
            setSuggestionActive(limitedOptions.length - 1);
            scrollTo('bottom');
          } else {
            setSuggestionActive(suggestionActive - 1);
            if (suggestionActive && suggestionActive >= MAX_SUGGESTION_INDEX) scrollTo('up');
          }
          break;
        }
        case 'Enter':
        case 'Space': {
          if (suggestionActive !== undefined) {
            setSuggestionActive(undefined);
            handleSelect(limitedOptions[suggestionActive]);
          }
          break;
        }
      }
    } else {
      setSuggestionActive(undefined);
    }
  };

  const decorateOption = useCallback(
    (option: string) => {
      const index = option.toLowerCase().indexOf(selectedValue.toLowerCase());

      return index !== -1 ? (
        <>
          <span>{option.slice(0, index)}</span>
          <span style={{ fontWeight: 700 }}>
            {option.slice(index, index + selectedValue.length)}
          </span>
          <span>{option.slice(index + selectedValue.length)}</span>
        </>
      ) : (
        option
      );
    },
    [selectedValue]
  );

  return (
    <StyledContainerSearch ref={containerRef}>
      <StyledSearchbar
        {...props}
        ref={searchRef}
        onIonClear={() => handleClear()}
        onIonChange={handleChange}
        onIonFocus={handleFocus}
        value={value || selectedValue}
        hasBorder={hasBorder}
        style={{ padding: '0 1px' }}
        showClearButton={disabled ? 'never' : 'always'}
        disabled={disabled}
        onKeyUp={onKeyUpHandler}
        mode={'md'}
      />
      {showSuggestions &&
        ReactDOM.createPortal(
          <StyledSuggestions ref={resultsRef} id="suggestions-list">
            {limitedOptions && limitedOptions.length > 0 ? (
              <StyledSuggestionList>
                {limitedOptions.map((option, i) => (
                  <StyledSuggestionItem
                    active={suggestionActive === i}
                    disableHover={suggestionActive !== undefined}
                    key={option.value}
                    className="allie-text-body3"
                    onClick={() => handleSelect(option)}
                  >
                    <span>{decorateOption(option.text)}</span>
                    {option.tag && <span className="tag-item">{option.tag}</span>}
                  </StyledSuggestionItem>
                ))}
                {(options || []).length > maxSuggestions && (
                  <StyledMoreResultsItem className="allie-text-body3">
                    {limitedResultText}
                  </StyledMoreResultsItem>
                )}
              </StyledSuggestionList>
            ) : addItem && limitedOptions?.length === 0 ? (
                <StyledAddItemList ref={resultsRef} id="suggestions-list">
                  <StyledAddItem
                    onClick={() => {
                      handleSelect({ value: selectedValue, text: selectedValue });
                      setShowSuggestions(false);
                    }}
                  >
                    <AllieAddIcon
                      style={{ fontSize: '20px', color: 'var(--allie-color-neutral-70)' }}
                    />
                    <AllieText type="body3" weight={500} color="neutral-70">
                      {addItemText} <strong>{selectedValue}</strong>
                    </AllieText>
                  </StyledAddItem>
                </StyledAddItemList>
              ) : (
                searchRef.current?.value?.length ? (
                  <StyledNoResultsItem className="allie-text-body3">
                    {noResultsText}
                  </StyledNoResultsItem>
                ) : null
              )}
          </StyledSuggestions>,
          document.body
        )}
    </StyledContainerSearch>
  );
};
